import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Interior & Exterior Painting",
  "Wood Refinishing & Stain Restoration",
  "Floor Deep Cleaning",
  "Floor Sales & Installation",
  "Upholstery & Sofa Cleaning",
  "Multiple Services",
  "Not Sure Yet",
];

const ease = [0.16, 1, 0.3, 1];
const inputCls =
  "w-full bg-white border-2 border-zinc-200 focus:border-red-600 focus:outline-none px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 transition-colors duration-200";

export default function QuoteForm({ selectedService, setSelectedService }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    preferred_date: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (selectedService) {
      setForm((f) => ({ ...f }));
    }
  }, [selectedService]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      toast.error("Please choose a service");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, service: selectedService }),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Quote request sent!", {
        description:
          "Thanks — we'll reach out shortly. Need us sooner? Call 325-234-5947.",
      });
      setForm({ name: "", phone: "", email: "", message: "", preferred_date: "" });
      setSelectedService("");
    } catch {
      toast.error("Something went wrong", {
        description: "Please try again or call us at 325-234-5947.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="bg-red-600 py-20 lg:py-32 relative overflow-hidden grain-overlay"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
        >
          <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-100 font-semibold mb-4">
            Free Quote — No Obligation
          </p>
          <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
            TELL US WHAT NEEDS TRANSFORMING.
          </h2>
          <p className="mt-6 text-red-100 text-base sm:text-lg leading-relaxed max-w-md">
            Describe the job and we'll get back to you with a straight,
            no-pressure quote. Prefer to talk? Call or text{" "}
            <a
              data-testid="quote-phone-link"
              href="tel:+13252345947"
              className="underline decoration-2 underline-offset-4 font-semibold text-white hover:text-zinc-950 transition-colors"
            >
              325-234-5947
            </a>
            .
          </p>
          <div className="mt-10 border-l-4 border-white pl-5">
            <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-red-100">
              Available 24/7 · We Answer Any Time
            </p>
            <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-red-100 mt-1">
              Est. 2004 · San Angelo, TX · Fully Insured
            </p>
          </div>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease }}
          className="bg-zinc-950 p-6 sm:p-10 shadow-2xl"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              data-testid="quote-form-name-input"
              required
              value={form.name}
              onChange={update("name")}
              placeholder="Full Name *"
              className={inputCls}
            />
            <input
              data-testid="quote-form-phone-input"
              required
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              placeholder="Phone Number *"
              className={inputCls}
            />
          </div>
          <input
            data-testid="quote-form-email-input"
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="Email Address *"
            className={`${inputCls} mt-4`}
          />
          <select
            data-testid="quote-form-service-select"
            required
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className={`${inputCls} mt-4 appearance-none ${!selectedService ? "text-zinc-400" : ""}`}
          >
            <option value="" disabled>
              Select a Service *
            </option>
            {serviceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            data-testid="quote-form-date-input"
            type="date"
            value={form.preferred_date}
            onChange={update("preferred_date")}
            className={`${inputCls} mt-4 ${!form.preferred_date ? "text-zinc-400" : ""}`}
          />
          <textarea
            data-testid="quote-form-message-input"
            rows={4}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us about the job — rooms, square footage, address…"
            className={`${inputCls} mt-4 resize-none`}
          />
          <button
            data-testid="quote-form-submit-btn"
            type="submit"
            disabled={sending}
            className="mt-6 w-full flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800/60 text-white px-7 py-4 font-mono-x text-sm font-semibold uppercase tracking-wider transition-colors duration-200 active:scale-[0.98]"
          >
            {sending ? "Sending…" : "Send My Free Quote Request"}
            <Send size={16} />
          </button>
          <p className="mt-4 text-center font-mono-x text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            No spam. No pressure. Just a straight quote.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
